import React, { useState, useEffect, useRef } from 'react'
import {
  Card,
  Table,
  TableCell,
  TableHead,
  TableRow,
  TableBody,
  TablePagination,
  Typography,
  Box,
  ListItemText
} from '@kyc-ui/react'
import dayjs from 'dayjs'
import AppAPI from 'src/api/app'
import { IAuditLogEntry, IAuditLogMeta } from 'src/typings/types'
import useTranslation from 'src/hooks/common/useTranslation'
import Loading from 'src/components/common/Loading'

interface IAuditLogProps {
  sharedCustomerId: number | null
  customerName?: string
}

const AuditLog: React.FC<IAuditLogProps> = ({ sharedCustomerId, customerName }) => {
  const t = useTranslation('usageReport')
  const [auditLogs, setAuditLogs] = useState<IAuditLogEntry[]>([])
  const [meta, setMeta] = useState<IAuditLogMeta | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const prevCustomerIdRef = useRef(sharedCustomerId)

  useEffect(() => {
    const customerChanged = prevCustomerIdRef.current !== sharedCustomerId
    if (customerChanged) {
      prevCustomerIdRef.current = sharedCustomerId
      setAuditLogs([])
      setMeta(null)
      setError(false)
      if (page !== 0) {
        setPage(0)
        return
      }
    }

    if (!sharedCustomerId) {
      setAuditLogs([])
      setMeta(null)
      setError(false)
      return
    }

    let cancelled = false
    setLoading(true)
    setError(false)
    AppAPI.getAuditLog(sharedCustomerId, page + 1, rowsPerPage, 1)
      .then((response) => {
        if (cancelled) return
        setAuditLogs(response.audits || [])
        setMeta(response.meta || null)
      })
      .catch(() => {
        if (cancelled) return
        setAuditLogs([])
        setMeta(null)
        setError(true)
      })
      .finally(() => {
        if (cancelled) return
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [sharedCustomerId, page, rowsPerPage])

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  if (!sharedCustomerId) {
    return null
  }

  return (
    <Card sx={{ px: 4, py: 6, mt: 4 }}>
      <ListItemText
        primary={<Typography variant="h6">{t('auditLog')}</Typography>}
        secondary={
          <Typography variant="body2" color="text.secondary">
            Source: 1 - change made from the project (usually via migration); 2 - change made by a user from the
            frontend
          </Typography>
        }
      />
      {loading ? (
        <Box sx={{ py: 4 }}>
          <Loading message={t('loadingAuditLog')} />
        </Box>
      ) : error ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="error">
            {t('auditLogError')}
          </Typography>
        </Box>
      ) : (
        <>
          <Table aria-label="Audit log table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '160px' }}>{t('dateTime')}</TableCell>
                <TableCell sx={{ width: '80px' }}>{t('source')}</TableCell>
                <TableCell>{t('actionPerformed')}</TableCell>
                <TableCell sx={{ width: '150px' }}>{t('performedBy')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {auditLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="text.secondary">
                      {t('noAuditLogs')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                auditLogs.map((log) => (
                  <TableRow key={log.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell component="th" scope="row">
                      {log.createdAt ? dayjs(log.createdAt).format('DD/MM/YYYY HH:mm') : ''}
                    </TableCell>
                    <TableCell>{log.source}</TableCell>
                    <TableCell>{log.message || ''}</TableCell>
                    <TableCell>{log.issuer || ''}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          {!!meta?.pages && meta.pages > 1 && (
            <TablePagination
              rowsPerPageOptions={[10, 20, 30, 50, 100]}
              component="div"
              count={meta.count}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </>
      )}
    </Card>
  )
}

export default AuditLog
